import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from '../api/client'
import { errorMessage } from '../api/ApiError'
import DataTable, { type Column } from '../components/DataTable'
import KpiGrid from '../components/KpiGrid'
import PanelExpediente, { BarrasDeDocumentos, LineaDeCierre } from '../components/expediente/PanelExpediente'
import { telefonoDe } from '../identidad'
import {
  ETAPAS, FASES, FILAS_POR_PAGINA, FILAS_POR_PEDIDO_EXCEL, TOPE_EXCEL, aExcel, aliasVisible,
  canalLegible, diaDeLima, diaDeLimaDe, horaLima, nombreDelArchivo, parametrosDelPedido,
  porcentaje, quedanMas, tituloDelDia, usuarioDe,
  type ConteoExpedientes, type Descarga, type FilaExpediente,
  type FiltroCompleto, type Filtros,
  type PaginaExpedientes,
} from '../expedientes'
import { TIPO_XLSX } from '../xlsx'

/**
 * Quién completó la documentación y quién no (12-oct-2026, pedido de Software),
 * y desde el 13-oct el EMBUDO (Ruben): todos los clientes que Software pasó o
 * que empezaron el flujo, con su fase —no le escribió al agente, en las
 * preguntas, checklist sin documentos, documentos parciales, entregó todo— y
 * las tarjetas de conversión arriba. Las fechas filtran por la ENTRADA.
 *
 * Supervisor y Admin. «Completó» es entregó todo lo que pide su checklist,
 * aunque tenga alertas: lo decide el servidor con el mismo cálculo de la Ficha.
 *
 * Paginado desde el 13-oct-2026 (Ruben): se ven los 30 expedientes más nuevos y
 * «Cargar más» suma de a 30. Los filtros los aplica el servidor —filtrar en el
 * navegador una página de 30 daría resultados incompletos—, y el conteo del
 * período viaja en un pedido aparte para que las primeras filas no esperen a
 * que se cuente todo. El Excel pide TODO lo filtrado, de a 500, aunque en
 * pantalla haya 30.
 *
 * 03-nov-2026 (Leslie y Ruben): «se ve muy cargado» y no se distinguía lo que
 * cerró el agente solo de lo que cerró con una persona. Lo aprobado sigue igual
 * —filtros, tarjetas del embudo, Excel con sus columnas en el mismo orden—, y la
 * tabla bajó a cinco columnas, agrupada por día y SIN BOTONES: tocar una fila
 * abre el panel del expediente, con los documentos y el chat. Teléfono, canal y
 * última actividad van al panel y siguen en el Excel.
 */
/** Los documentos de la fila: «5/6» y una barra por requisito. El nombre de
 *  cada uno va en el tooltip y en el panel: los chips con nombre eran lo que más
 *  cargaba la fila (Ruben, 03-nov-2026). */
function Documentos({ fila }: { fila: FilaExpediente }) {
  return <BarrasDeDocumentos fila={fila} />
}

const COMPLETO: { valor: FiltroCompleto; label: string }[] = [
  { valor: 'todos', label: 'Todos' },
  { valor: 'completaron', label: 'Completaron' },
  { valor: 'no_completaron', label: 'No completaron' },
  { valor: 'por_descargar', label: 'Por descargar' },
]

/** Espera antes de buscar mientras se escribe: un pedido por palabra, no por tecla. */
const ESPERA_BUSQUEDA_MS = 400

export default function Expedientes() {
  const [seleccion, setSeleccion] = useState<FilaExpediente | null>(null)
  const [desde, setDesde] = useState(() => diaDeLima(Date.now(), 30))
  const [hasta, setHasta] = useState(() => diaDeLima(Date.now()))
  const [completo, setCompleto] = useState<FiltroCompleto>('todos')
  const [fase, setFase] = useState('')
  const [canal, setCanal] = useState('')
  const [texto, setTexto] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [filas, setFilas] = useState<FilaExpediente[]>([])
  const [siguiente, setSiguiente] = useState<string | null>(null)
  const [cuenta, setCuenta] = useState<ConteoExpedientes | null>(null)
  const [loading, setLoading] = useState(true)
  const [cargandoMas, setCargandoMas] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportando, setExportando] = useState<number | null>(null)
  // Cada cambio de filtros es un pedido nuevo: la respuesta de uno anterior que
  // llegue tarde no pisa la tabla ni el conteo de los filtros actuales.
  const pedido = useRef(0)

  useEffect(() => {
    const t = setTimeout(() => setBusqueda(texto), ESPERA_BUSQUEDA_MS)
    return () => clearTimeout(t)
  }, [texto])

  const filtros: Filtros = { desde, hasta, canal, completo, fase, texto: busqueda }
  const clave = JSON.stringify(filtros)

  const cargar = useCallback(async () => {
    const f = JSON.parse(clave) as Filtros
    const n = ++pedido.current
    setLoading(true)
    setCuenta(null)
    apiClient.get<ConteoExpedientes>('/expedientes', {
      params: parametrosDelPedido(f, { vista: 'conteo' }),
    }).then((res) => { if (n === pedido.current) setCuenta(res.data) })
      .catch(() => { /* sin conteo la tabla igual se ve; el subtítulo dice «contando» */ })
    try {
      const res = await apiClient.get<PaginaExpedientes>('/expedientes', {
        params: parametrosDelPedido(f, { limite: FILAS_POR_PAGINA }),
      })
      if (n !== pedido.current) return
      setFilas(res.data.filas ?? [])
      setSiguiente(res.data.siguiente ?? null)
      setError(null)
    } catch (err) {
      if (n === pedido.current) setError(errorMessage(err, 'No se pudo cargar el reporte de expedientes.'))
    } finally {
      if (n === pedido.current) setLoading(false)
    }
  }, [clave])

  useEffect(() => { void cargar() }, [cargar])

  const cargarMas = async () => {
    if (!siguiente) return
    const n = pedido.current
    setCargandoMas(true)
    try {
      const res = await apiClient.get<PaginaExpedientes>('/expedientes', {
        params: parametrosDelPedido(filtros, { limite: FILAS_POR_PAGINA, cursor: siguiente }),
      })
      if (n !== pedido.current) return
      setFilas((previas) => [...previas, ...(res.data.filas ?? [])])
      setSiguiente(res.data.siguiente ?? null)
    } catch (err) {
      if (n === pedido.current) setError(errorMessage(err, 'No se pudieron cargar más expedientes.'))
    } finally {
      setCargandoMas(false)
    }
  }

  // El Excel lleva TODO lo filtrado, no lo que está en pantalla: lo pide de a
  // 500 siguiendo el cursor, hasta el tope.
  const descargar = async () => {
    setExportando(0)
    try {
      const todas: FilaExpediente[] = []
      let cursor: string | null = null
      do {
        const res: { data: PaginaExpedientes } = await apiClient.get<PaginaExpedientes>('/expedientes', {
          params: parametrosDelPedido(filtros, { limite: FILAS_POR_PEDIDO_EXCEL, cursor }),
        })
        todas.push(...(res.data.filas ?? []))
        cursor = res.data.siguiente ?? null
        setExportando(todas.length)
      } while (cursor && todas.length < TOPE_EXCEL)
      const ctx = {
        desde, hasta, canal, completo, fase, texto: busqueda, generadoMs: Date.now(),
        totalDelPeriodo: cuenta?.total,
      }
      const filasDelArchivo = todas.slice(0, TOPE_EXCEL)
      const url = URL.createObjectURL(new Blob([aExcel(filasDelArchivo, ctx)], { type: TIPO_XLSX }))
      const a = document.createElement('a')
      a.href = url
      a.download = nombreDelArchivo(ctx)
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(errorMessage(err, 'No se pudo armar el Excel.'))
    } finally {
      setExportando(null)
    }
  }

  // La fila descargada cambia de paso sin volver a pedir la página: lo que
  // dice la fila y lo que dice el panel salen de la misma respuesta.
  const alDescargar = useCallback((descarga: Descarga) => {
    const id = seleccion?.conversation_id
    if (!id) return
    const marcar = (f: FilaExpediente) => (f.conversation_id === id
      ? { ...f, descarga, por_descargar: false } : f)
    setFilas((previas) => previas.map(marcar))
    setSeleccion((s) => (s ? marcar(s) : s))
  }, [seleccion?.conversation_id])

  const ahora = Date.now()
  const columnas: Column<FilaExpediente>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      cell: (r) => (
        <span className="flex flex-col">
          {r.nombre
            ? <span className="font-semibold">{r.nombre}</span>
            : <span className="text-ink-faint">Sin identificar</span>}
          {/* Sin número, el usuario de WhatsApp es lo único que lo identifica
              (Ruben, 13-oct-2026: «debe colocar el alias para saberlo»). El
              teléfono pasó al panel; el alias se queda en la fila. */}
          {!telefonoDe(r) && r.alias && (
            <span className="text-micro font-semibold text-ink-soft" title="Sin número · usuario de WhatsApp">
              {aliasVisible(r)}
            </span>
          )}
        </span>
      ),
      sub: (r) => (r.dni ? `DNI ${r.dni}` : null),
    },
    {
      key: 'entrada',
      header: 'Entrada',
      // El día lo dice el encabezado del grupo: acá va la hora.
      cell: (r) => <span className="font-mono">{horaLima(r.entrada)}</span>,
    },
    {
      key: 'fase',
      header: 'Fase',
      // El badge en una línea y debajo UNA línea de texto: cómo cerró y en qué
      // paso está, o la pregunta, o las alertas (Ruben, 13-oct y 03-nov-2026).
      cell: (r) => (
        <span className="flex flex-col items-start gap-1">
          <span className={`whitespace-nowrap ${r.fase === 'completo' ? 'badge-green'
            : r.fase === 'no_escribio' ? 'badge-red' : 'badge-amber'}`}>
            {r.fase_label}
          </span>
          {r.como_cerro ? <LineaDeCierre fila={r} />
            : r.pregunta ? (
              <span className="text-micro text-ink-muted">Pregunta: {r.pregunta}</span>
            ) : r.alertas.length > 0 && (
              <span className="text-micro font-bold text-warn" title={r.alertas.join('\n')}>
                {r.alertas.length} alerta{r.alertas.length === 1 ? '' : 's'}
              </span>
            )}
        </span>
      ),
    },
    {
      key: 'documentos',
      header: 'Documentos',
      cell: (r) => <Documentos fila={r} />,
    },
    {
      key: 'asesor',
      header: 'Asesor',
      cell: (r) => usuarioDe(r.asesor) || '—',
      sub: (r) => (r.atendido_por_persona ? 'Lo atendió una persona'
        : r.derivado ? 'Derivado, sin atender' : null),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="card flex flex-wrap items-end gap-4 px-5 py-4">
        <label className="flex flex-col gap-1">
          <span className="label">Desde</span>
          <input type="date" className="input" value={desde} max={hasta || undefined}
                 onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Hasta</span>
          <input type="date" className="input" value={hasta} min={desde || undefined}
                 onChange={(e) => setHasta(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Canal</span>
          <select className="input" value={canal} onChange={(e) => setCanal(e.target.value)}>
            <option value="">Todos</option>
            {/* Los canales del período; el elegido sigue aunque el conteo no llegue. */}
            {[...new Set([...(cuenta?.canales ?? []), ...(canal ? [canal] : [])])].map((c) => (
              <option key={c} value={c}>{canalLegible(c)}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="label">Fase</span>
          <select className="input" value={fase} onChange={(e) => setFase(e.target.value)}>
            <option value="">Todas</option>
            {FASES.map((f) => <option key={f.valor} value={f.valor}>{f.label}</option>)}
          </select>
        </label>
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className="label">Buscar</span>
          <input type="text" className="input" placeholder="Nombre, DNI o teléfono"
                 value={texto} onChange={(e) => setTexto(e.target.value)} />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {COMPLETO.map((o) => (
            <button key={o.valor}
                    className={completo === o.valor ? 'pill-filter-active' : 'pill-filter'}
                    onClick={() => setCompleto(o.valor)}>
              {o.label}
              {o.valor === 'por_descargar' && cuenta?.por_descargar !== undefined
                && ` ${cuenta.por_descargar}`}
            </button>
          ))}
        </div>
      </div>

      {/* El embudo: cuántos llegaron AL MENOS a cada etapa, sobre los que
          entraron. Mismo cálculo que `embudo.etapas` del servidor. */}
      <KpiGrid
        columns={5}
        kpis={ETAPAS.map((e) => {
          const n = cuenta?.etapas?.[e.valor]
          return {
            label: e.label,
            value: n ?? '—',
            hint: n === undefined ? 'Contando…'
              : e.valor === 'entraron' ? 'Pases de Software y flujos empezados'
                : e.valor === 'completaron' && cuenta?.por_descargar !== undefined
                  ? `${porcentaje(n, cuenta?.etapas?.entraron ?? 0)} · ${cuenta.por_descargar} por descargar`
                  : `${porcentaje(n, cuenta?.etapas?.entraron ?? 0)} de los que entraron`,
          }
        })}
      />

      <div className="flex flex-wrap items-start gap-4">
      <div className="min-w-0 flex-[999_1_560px]">
      <DataTable
        title="Expedientes"
        subtitle={loading ? 'Cargando…'
          : cuenta
            ? `${cuenta.total} expediente${cuenta.total === 1 ? '' : 's'} · `
              + `${cuenta.completaron} completaron · ${cuenta.no_completaron} no completaron`
              + (cuenta.total > filas.length ? ` · se ven los ${filas.length} más recientes` : '')
            : `${filas.length} cargados · contando el período…`}
        columns={columnas}
        rows={filas}
        rowKey={(r) => r.conversation_id}
        loading={loading}
        error={error}
        onRetry={() => void cargar()}
        // Tocar la fila abre el panel con los documentos y el chat. La
        // conversación completa se abre desde el panel (03-nov-2026).
        onRowClick={(r) => setSeleccion(r)}
        highlightKey={seleccion?.conversation_id ?? ''}
        grupo={(r) => tituloDelDia(diaDeLimaDe(r.entrada), ahora)}
        emptyTitle="Sin expedientes"
        emptyMessage="Ningún cliente llegó a tener expediente con estos filtros."
        action={(
          <button className="btn-primary" onClick={() => void descargar()}
                  disabled={loading || filas.length === 0 || exportando !== null}>
            {exportando === null ? 'Descargar Excel' : `Preparando… ${exportando} filas`}
          </button>
        )}
      />
      </div>
      {seleccion && (
        <div className="sticky top-4 flex max-h-[calc(100vh-120px)] min-w-0 flex-[1_1_360px] flex-col">
          <PanelExpediente
            fila={seleccion}
            modo="supervision"
            onCerrar={() => setSeleccion(null)}
            onDescargado={alDescargar}
          />
        </div>
      )}
      </div>

      {!loading && !error && quedanMas(filas.length, cuenta?.total ?? null, siguiente) && (
        <button className="btn-ghost self-center" onClick={() => void cargarMas()} disabled={cargandoMas}>
          {cargandoMas ? 'Cargando…' : `Cargar ${FILAS_POR_PAGINA} más`}
        </button>
      )}
    </div>
  )
}
